import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings, ArrowRight, Users, ChartBar, Info, Plus
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

const GroupSelector = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user/groups`);
      setGroups(response.data.groups || []);
    } catch (error) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (groupId) => {
    navigate(`/groups/${groupId}/dashboard`);
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="group-selector">
      <Sidebar />
      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary flex items-center gap-3">
                <Buildings size={28} className="text-gold" />
                Group Structures
              </h1>
              <p className="text-text-tertiary mt-1">
                View aggregate compliance across your group divisions
              </p>
            </div>
            {user?.is_navigate_admin && (
              <button
                onClick={() => navigate('/navigate-admin')}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm font-medium hover:bg-gold/90 transition-colors"
              >
                <Plus size={20} />
                Manage Groups
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-gold font-mono">Loading groups...</div>
            </div>
          ) : groups.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-20 h-20 bg-background-surface rounded-full flex items-center justify-center mx-auto mb-6">
                <Buildings size={40} className="text-text-tertiary" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
                No Groups Available
              </h2>
              <p className="text-text-tertiary max-w-md mx-auto mb-6">
                {user?.is_navigate_admin 
                  ? "You haven't created any groups yet. Create a group from the Navigate Admin portal to get started."
                  : "You don't have access to any group structures. Contact your administrator to be assigned as a Group Admin."}
              </p>
              {user?.is_navigate_admin && (
                <button
                  onClick={() => navigate('/navigate-admin')}
                  className="px-6 py-3 bg-gold text-gold-foreground rounded-sm font-medium hover:bg-gold/90 transition-colors"
                >
                  Go to Navigate Admin
                </button>
              )}
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group, idx) => (
                <motion.div
                  key={group.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => handleGroupSelect(group.id)}
                  className="bg-background-surface border border-border rounded-sm p-6 cursor-pointer hover:border-gold/50 transition-all group"
                  data-testid={`group-card-${group.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gold/10 rounded-sm flex items-center justify-center border border-gold/30">
                      <Buildings size={24} className="text-gold" />
                    </div>
                    <span className="px-2 py-1 rounded-sm text-xs font-mono bg-background border border-border text-text-tertiary">
                      {group.division_count || 0} divisions
                    </span>
                  </div>

                  <h3 className="text-lg font-heading font-semibold text-text-primary mb-2 group-hover:text-gold transition-colors">
                    {group.name}
                  </h3>
                  
                  {group.description && (
                    <p className="text-sm text-text-tertiary mb-4 line-clamp-2">
                      {group.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-xs text-text-tertiary">
                      <Users size={14} />
                      <span>{group.admin_user_ids?.length || 1} admin(s)</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gold group-hover:translate-x-1 transition-transform">
                      View Dashboard
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Info Section */}
          {groups.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 p-4 bg-gold/5 border border-gold/20 rounded-sm"
            >
              <div className="flex items-start gap-3">
                <Info size={20} className="text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gold mb-1">About Group Dashboards</h4>
                  <p className="text-xs text-text-tertiary">
                    Group dashboards provide an aggregate view of compliance posture across all divisions in a holding company structure. 
                    You can view overall maturity scores, risk appetite gaps, and top risks. Create periodic reports for board presentations 
                    and download them as PDF executive summaries.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupSelector;
